import type { WorkbookSopUpdate } from '@erp/rpc';
import type { WorkbookDefinition } from '@erp/shared-types';
import { processOutline } from '@erp/workbooks';
import type { SopUpdatePatch } from './workbookEdits';

type ProcessStep = WorkbookDefinition['processSteps'][number];

/**
 * The Instructions an SOP update can rewrite: up for review only because their
 * Linked items changed, with text and Linked items. One the user also edited
 * (`both-edited`) holds intent the settings lack, so Generation applies it
 * instead. Mirrors the server's candidates.
 */
export function sopUpdateCandidates(definition: WorkbookDefinition): string[] {
  const outline = processOutline(definition);
  return definition.processSteps
    .filter(
      (step) =>
        step.reviewStatus === 'configuration-edited' &&
        step.instructions.trim() !== '' &&
        outline.linkedItemIds(step.id).size > 0,
    )
    .map((step) => step.id);
}

/** A proposal still rewrites the step as it is: nobody edited its text or title since. */
function stillApplies(step: ProcessStep, update: WorkbookSopUpdate): boolean {
  return (
    step.reviewStatus === 'configuration-edited' &&
    step.instructions === update.baseInstructions &&
    (step.title ?? null) === update.baseTitle
  );
}

/** The pending proposal of each Instruction it still applies to, by step id. */
export function applicableSopUpdates(
  definition: WorkbookDefinition,
  updates: readonly WorkbookSopUpdate[],
): ReadonlyMap<string, WorkbookSopUpdate> {
  const steps = new Map(definition.processSteps.map((step) => [step.id, step]));
  return new Map(
    updates.flatMap((update) => {
      const step = steps.get(update.stepId);
      return update.status === 'proposed' && step && stillApplies(step, update)
        ? [[update.stepId, update] as const]
        : [];
    }),
  );
}

/**
 * Each Stale Instruction whose newest review reached no verdict (the AI check or
 * the writer was unavailable), so the user knows why it was not updated.
 */
export function unavailableSopReviews(
  definition: WorkbookDefinition,
  updates: readonly WorkbookSopUpdate[],
): ReadonlyMap<string, WorkbookSopUpdate> {
  const candidates = new Set(sopUpdateCandidates(definition));
  return new Map(
    updates.flatMap((update) =>
      update.status === 'unavailable' && candidates.has(update.stepId)
        ? [[update.stepId, update] as const]
        : [],
    ),
  );
}

/** The edit an accepted proposal makes: its text, its title when it proposes one, and its provenance. */
export function sopUpdatePatch(update: WorkbookSopUpdate): SopUpdatePatch {
  return {
    instructions: update.proposedInstructions,
    ...(update.proposedTitle === null ? {} : { title: update.proposedTitle }),
    provenance: {
      kind: 'ai-updated',
      revision: update.revision,
      updateId: update.id,
    },
  };
}
